import React, { useState } from 'react';
import { FiCalendar, FiAlertCircle, FiChevronDown } from 'react-icons/fi';
import { FaTrain } from 'react-icons/fa';
import Input from '../../Common/Input';
import Button from '../../Common/Button';
import styles from './TrainStatusForm.module.css';

interface TrainStatusFormProps {
  onSubmit: (trainNumber: string, date: string) => void;
  isLoading: boolean;
}

const TrainStatusForm: React.FC<TrainStatusFormProps> = ({ onSubmit, isLoading }) => {
  const [trainNumber, setTrainNumber] = useState('');
  const [selectedDateOption, setSelectedDateOption] = useState('today');
  const [customDate, setCustomDate] = useState('');
  const [errors, setErrors] = useState<{ trainNumber?: string; date?: string }>({});

  // Get formatted dates in dd-mm-yyyy format
  const getDateOptions = () => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const twoDaysBefore = new Date(today);
    twoDaysBefore.setDate(twoDaysBefore.getDate() - 2);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    // Format as dd-mm-yyyy for backend
    const formatDate = (date: Date) => {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    const formatDisplayDate = (date: Date) => {
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    };

    return {
      twoDaysBefore: {
        value: formatDate(twoDaysBefore),
        display: formatDisplayDate(twoDaysBefore),
        label: '2 Days Before',
      },
      yesterday: {
        value: formatDate(yesterday),
        display: formatDisplayDate(yesterday),
        label: 'Yesterday',
      },
      today: {
        value: formatDate(today),
        display: formatDisplayDate(today),
        label: 'Today',
      },
      tomorrow: {
        value: formatDate(tomorrow),
        display: formatDisplayDate(tomorrow),
        label: 'Tomorrow',
      },
    };
  };

  const dateOptions = getDateOptions();

  const validateForm = (): boolean => {
    const newErrors: { trainNumber?: string; date?: string } = {};

    if (!trainNumber.trim()) {
      newErrors.trainNumber = 'Please enter a train number';
    } else if (!/^\d{5}$/.test(trainNumber)) {
      newErrors.trainNumber = 'Train number must be 5 digits';
    }

    if (selectedDateOption === 'custom' && !customDate) {
      newErrors.date = 'Please select a date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      let dateToSubmit = '';
      
      if (selectedDateOption === 'custom') {
        // Convert YYYY-MM-DD to dd-mm-yyyy
        const [year, month, day] = customDate.split('-');
        dateToSubmit = `${day}-${month}-${year}`;
      } else {
        dateToSubmit = dateOptions[selectedDateOption as keyof typeof dateOptions].value;
      }

      onSubmit(trainNumber, dateToSubmit);
    }
  };

  const handleTrainNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 5);
    setTrainNumber(value);
    if (errors.trainNumber) {
      setErrors({ ...errors, trainNumber: undefined });
    }
  };

  const handleDateOptionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedDateOption(e.target.value);
    if (errors.date) {
      setErrors({ ...errors, date: undefined });
    }
  };

  const handleCustomDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomDate(e.target.value);
    if (errors.date) {
      setErrors({ ...errors, date: undefined });
    }
  };

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <div className={styles.formGrid}>
        {/* Train Number Input */}
        <div className={styles.inputWrapper}>
          <Input
            type="text"
            value={trainNumber}
            onChange={handleTrainNumberChange}
            placeholder="Enter 5-digit train number"
            label="Train Number"
            error={errors.trainNumber}
            icon={<FaTrain />}
            fullWidth
            maxLength={5}
            autoFocus
          />
        </div>

        {/* Date Selection */}
        <div className={styles.inputWrapper}>
          <label className={styles.label}>Journey Date</label>
          <div className={styles.dateSelectWrapper}>
            <FiCalendar className={styles.dateIcon} />
            <select
              value={selectedDateOption}
              onChange={handleDateOptionChange}
              className={styles.dateSelect}
            >
              <option value="twoDaysBefore">
                2 Days Before ({dateOptions.twoDaysBefore.display})
              </option>
              <option value="yesterday">
                Yesterday ({dateOptions.yesterday.display})
              </option>
              <option value="today">
                Today ({dateOptions.today.display})
              </option>
              <option value="tomorrow">
                Tomorrow ({dateOptions.tomorrow.display})
              </option>
              <option value="custom">Choose from Calendar</option>
            </select>
            <FiChevronDown className={styles.dropdownIcon} />
          </div>
          {errors.date && <span className={styles.errorText}>{errors.date}</span>}
        </div>
      </div>

      {/* Custom Date Picker */}
      {selectedDateOption === 'custom' && (
        <div className={styles.customDateWrapper}>
          <Input
            type="date"
            value={customDate}
            onChange={handleCustomDateChange}
            label="Select Custom Date"
            error={errors.date}
            icon={<FiCalendar />}
            fullWidth
          />
        </div>
      )}

      {(errors.trainNumber || errors.date) && (
        <div className={styles.errorMessage}>
          <FiAlertCircle />
          <span>{errors.trainNumber || errors.date}</span>
        </div>
      )}

      <Button
        type="submit"
        variant="secondary"
        size="lg"
        fullWidth
        isLoading={isLoading}
      >
        {isLoading ? 'Fetching Status...' : 'Check Train Status'}
      </Button>

      <div className={styles.hint}>
        <p>💡 Get real-time train running status with station-wise updates</p>
      </div>
    </form>
  );
};

export default TrainStatusForm;